import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { BarShapeProps, TooltipContentProps } from "recharts";
import type { OverviewTopicWeeklyRow } from "@nexus/contracts";
import { Button, ToggleGroup, ToggleGroupItem } from "@nexus/ui";
import { formatTopicTitle } from "../OverviewChart/chartPrimitives.js";
import { RoundedTopBar } from "../OverviewChart/RoundedTopBar.js";
import { downloadText } from "../../lib/download.js";
import { CHART_CSV_MIME } from "../../lib/exportChartCsv.js";
import {
  buildOvertimeChartCsv,
  overtimeChartCsvFilename,
} from "../../lib/exportOvertimeChartCsv.js";
import { buildOvertimeData, shareOfWeekPercent } from "./buildOvertimeData.js";

// 5 series colors from the design token palette — matches Figma "Overtime View" spec.
// chart5 (error red) is intentionally skipped; chart6 = ext.ai[500] (#00F5C8) fills slot 5.
const TOPIC_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-6)",
] as const;

// Minimum pixel width per week-bar so bars stay readable at any data volume.
// 10 weeks visible at 100% → each visible bar is container-width / 10.
const VISIBLE_WEEKS = 10;

interface WeekDatum {
  label: string;
  [topic: string]: string | number;
}

/** Highest stacked segment with a positive value for this week — the visible bar top. */
function topStackSegmentIndex(datum: WeekDatum, topics: readonly string[]): number {
  for (let i = topics.length - 1; i >= 0; i--) {
    const value = datum[topics[i]!];
    if (typeof value === "number" && value > 0) return i;
  }
  return -1;
}

function createStackedBarShape(segmentIndex: number, topics: readonly string[]) {
  return function StackedBarShape({ x, y, width, height, fill, payload }: BarShapeProps) {
    if (x == null || y == null || width == null || height == null || height <= 0 || width <= 0) {
      return null;
    }
    const datum = payload as WeekDatum;
    if (segmentIndex === topStackSegmentIndex(datum, topics)) {
      return <RoundedTopBar x={x} y={y} width={width} height={height} fill={fill} />;
    }
    return <rect x={x} y={y} width={width} height={height} fill={fill} />;
  };
}

interface TopConceptsOvertimeChartProps {
  weeklyRows: OverviewTopicWeeklyRow[];
  /** Brand bucket to scope the stacked bars to. Defaults to the tenant's own brand ("you"),
   * matching the single-brand Retool tile. The page owns this value. */
  bucket?: OverviewTopicWeeklyRow["bucket"];
}

// ─── Tooltip ─────────────────────────────────────────────────────────────────
function ChartTooltip({
  active,
  payload,
  label,
  topics,
  mode,
}: TooltipContentProps<number, string> & {
  topics: string[];
  mode: "raw" | "normalized";
}) {
  if (!active || !payload?.length) return null;
  const datum = payload[0]?.payload as WeekDatum | undefined;
  if (!datum) return null;

  return (
    <div className="rounded-lg border bg-card p-3 text-xs shadow-md">
      <p className="font-medium text-foreground">{label}</p>
      <p className="mb-2 text-[10px] leading-tight text-muted-foreground">
        {mode === "normalized"
          ? "Share of the week's assets, by concept"
          : "Distinct assets per concept"}
      </p>
      {topics.map((topic, i) => {
        const value = datum[topic];
        if (typeof value !== "number") return null;
        return (
          <div key={topic} className="flex items-center gap-2">
            <span
              className="h-2 w-2 shrink-0 rounded-sm"
              style={{ background: TOPIC_COLORS[i] }}
            />
            <span className="text-muted-foreground">{formatTopicTitle(topic)}</span>
            <span className="font-medium text-foreground">
              {mode === "normalized"
                ? `${Math.round(value)}%`
                : value.toLocaleString()}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function TopConceptsOvertimeChart({
  weeklyRows,
  bucket = "you",
}: TopConceptsOvertimeChartProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"raw" | "normalized">("raw");

  // Rank top concepts and pivot per week, scoped to a single brand bucket and ordered
  // chronologically (oldest = "Week 1"). See buildOvertimeData for the data contract.
  const { topTopics, weeks } = buildOvertimeData(weeklyRows, bucket);

  const data: WeekDatum[] = weeks.map(({ label, values }) => {
    const weekTotal = topTopics.reduce((sum, topic) => sum + (values[topic] ?? 0), 0);

    const datum: WeekDatum = { label };
    for (const topic of topTopics) {
      const raw = values[topic] ?? 0;
      datum[topic] = mode === "normalized" ? shareOfWeekPercent(raw, weekTotal) : raw;
    }
    return datum;
  });

  const axisTick = {
    fontSize: 11,
    fill: "var(--muted-foreground)",
  } as const;

  const isEmpty = topTopics.length === 0 || data.length === 0;

  // Exports both encodings of the plotted series — same `weeks`/`topTopics` the
  // bars and legend render, so the CSV can't drift from the visible top-N.
  const handleExport = useCallback(() => {
    downloadText(
      buildOvertimeChartCsv(weeks, topTopics, {
        week: t("overview.chart.csv.week", "Week"),
        weekStart: t("overview.chart.csv.weekStart", "Week start"),
        shareColumn: (concept) =>
          t("overview.chart.csv.shareOfWeekColumn", {
            concept,
            defaultValue: "{{concept}} (% of week)",
          }),
      }),
      overtimeChartCsvFilename(),
      CHART_CSV_MIME,
    );
  }, [t, weeks, topTopics]);

  return (
    <div
      data-testid="overtime-chart"
      data-chart-mode={mode}
      className="rounded-[14px] border border-border bg-card p-[21px]"
    >
      <div className="flex items-center justify-between gap-4">
        <h3 className="text-[15px] font-medium tracking-[-0.2px] text-foreground">
          Overtime View · Top Concepts
        </h3>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            iconStart="download"
            onClick={handleExport}
            disabled={isEmpty}
            data-testid="overview-overtime-export"
            className="h-7 shrink-0 rounded-[6px] px-2 py-1.5 text-[12px] text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {t("overview.chart.export", "Export")}
          </Button>
          <ToggleGroup
            type="single"
            value={mode}
            onValueChange={(v) => {
              if (v) setMode(v as "raw" | "normalized");
            }}
            variant="pill"
            aria-label="display mode"
            data-testid="raw-normalized-toggle"
          >
            <ToggleGroupItem value="raw" size="sm" aria-label="Raw">
              Raw
            </ToggleGroupItem>
            <ToggleGroupItem value="normalized" size="sm" aria-label="Normalized">
              Normalized
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      {/* Suppressing the mousedown default keeps a click from focusing the recharts
        surface or a bar group, which the base `outline-ring` rule would then ring. See
        the fuller note in OverviewChart/views/BarsView.tsx. */}
      <div
        className="mt-4 h-[360px] w-full overflow-x-auto overflow-y-hidden"
        onMouseDown={(event) => event.preventDefault()}
      >
        {isEmpty ? (
          <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
            No weekly topic data for the current filters.
          </div>
        ) : (
          // Horizontal scroll track: each of N weeks gets at least (100/VISIBLE_WEEKS)%
          // of the visible container width so bars are always readable.
          <div
            data-testid="chart-scroll-track"
            className="h-full"
            style={{
              width: `${Math.max(100, (data.length / VISIBLE_WEEKS) * 100)}%`,
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                barSize={50}
                margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
              >
                <CartesianGrid syncWithTicks stroke="var(--border)" vertical={false} />
                <XAxis
                  dataKey="label"
                  interval={0}
                  tick={axisTick}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={axisTick}
                  tickLine={false}
                  axisLine={false}
                  {...(mode === "normalized"
                    ? { tickFormatter: (v: number) => `${Math.round(v)}%`, domain: [0, 100] }
                    : {})}
                />
                <Tooltip
                  cursor={false}
                  content={(props) => (
                    <ChartTooltip
                      {...(props as TooltipContentProps<number, string>)}
                      topics={topTopics}
                      mode={mode}
                    />
                  )}
                />
                {topTopics.map((topic, i) => (
                  <Bar
                    key={topic}
                    dataKey={topic}
                    stackId="a"
                    fill={TOPIC_COLORS[i]}
                    shape={createStackedBarShape(i, topTopics)}
                    isAnimationActive={false}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Legend */}
      {!isEmpty && (
        <div className="mt-3 flex flex-wrap justify-center gap-x-4 gap-y-1.5">
          {topTopics.map((topic, i) => (
            <div key={topic} className="flex items-center gap-1.5">
              <span
                className="h-3 w-3 shrink-0 rounded-[3px]"
                style={{ background: TOPIC_COLORS[i] }}
              />
              <span className="text-[11px] text-muted-foreground">{formatTopicTitle(topic)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
